import { useCallback, useEffect, useRef, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";

export interface SiteFeatures {
  user_extension: boolean;
}

const DEFAULT_FEATURES: SiteFeatures = { user_extension: false };

export const useGetFeatures = () => {
  const [features, setFeatures] = useState<SiteFeatures>(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  // Returns whether the fetch actually succeeded — the guard below relies on
  // this to tell a real failure apart from "nothing to do yet".
  const getFeatures = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const url = getApiUrl("wp-json/smassets/v1/features");
      const response = await fetchWithTokenRefresh(
        url,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.authToken ?? ""}`,
          },
        },
        () => refreshUserToken(user?.refreshToken ?? "")
      );
      if (response.ok) {
        const json = await response.json();
        setFeatures({ user_extension: Boolean(json?.user_extension) });
        return true;
      }
      return false;
    } catch {
      // fall back to defaults on network/parse errors
      return false;
    } finally {
      setLoading(false);
    }
  }, [getApiUrl, user?.authToken, user?.refreshToken, refreshUserToken]);

  // Fetch once per mount, with one bounded retry on failure. Gate on success
  // (not "attempted") — if `getFeatures`/`user?.authToken` change while a
  // retry is pending, cleanup cancels that timer, but since we haven't
  // succeeded yet the next effect run isn't blocked and starts a fresh
  // attempt immediately (with the current token) instead of silently losing
  // the only retry.
  const hasSucceeded = useRef(false);
  useEffect(() => {
    if (!user?.authToken || hasSucceeded.current) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retried = false;

    const attempt = () => {
      getFeatures().then((success) => {
        if (cancelled) return;
        if (success) {
          hasSucceeded.current = true;
        } else if (!retried) {
          retried = true;
          retryTimer = setTimeout(attempt, 3000);
        }
      });
    };
    attempt();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [getFeatures, user?.authToken]);

  return { features, featuresLoading: loading };
};
